import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import SpotlightBeforeAfter from "./SpotlightBeforeAfter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Four transformation stories — each has an editorial story panel + the drag slider.
// Images are fetched from admin-uploaded media (transform-N-before/after).
const PAIRS = [
  {
    key: "transform-1",
    label: "Cast-Iron Storefront · SoHo",
    kicker: "Pressure Washing",
    headline: "Twenty years of grime, one dawn shift.",
    body:
      "Ground-level facades on Broadway see fifteen thousand pedestrians a day. Rain lifts the film for an hour; the next commute paints it back on. We hot-water surface-clean the sidewalk and lower brick, then apply an anti-graffiti sacrificial coat so the next tag comes off with warm water.",
    stat: { value: "1,800 psi", label: "Hot water at 200°F" },
  },
  {
    key: "transform-2",
    label: "Restaurant Storefront Windows · Flatiron",
    kicker: "DI Water Window Cleaning",
    headline: "Streak-free. Every pane. No exceptions.",
    body:
      "Traditional soap-and-squeegee leaves trace minerals that fog under the next rain. Our deionised water-fed poles drop total dissolved solids to zero — so when the glass dries, it dries perfectly clear. No ladders, no bucket rings, no callbacks.",
    stat: { value: "0 TDS", label: "Total dissolved solids" },
  },
  {
    key: "transform-3",
    label: "Retail Plaza Parking · Long Island",
    kicker: "Sealcoat & Line Striping",
    headline: "First impressions live in the parking lot.",
    body:
      "Ninety percent of shoppers form an opinion of your tenants before they've opened a door. We sealcoat, prime, and stripe with two-coat epoxy — municipal-spec handicap symbols, directional arrows, and stall lines that read chalk-line straight in every parking-app photo.",
    stat: { value: "90 min", label: "Drive-on cure time" },
  },
  {
    key: "transform-4",
    label: "Alley Brick Facade · Williamsburg",
    kicker: "Graffiti Removal",
    headline: "Off the wall before your first tenant sees it.",
    body:
      "Fresh tags cost you rent. We treat 90% of graffiti within twenty-four hours of report — poultice for painted brick, low-pressure warm water for uncoated masonry, and a sacrificial anti-graffiti barrier so the next attack is a two-hour rinse instead of a full restoration.",
    stat: { value: "24 hr", label: "Removal SLA" },
  },
];

export default function Transformation() {
  const [media, setMedia] = useState({});
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const out = {};
      for (const [k, v] of Object.entries(raw)) {
        out[k] = v.startsWith("http") ? v : `${base}${v}`;
      }
      setMedia(out);
    }).catch(() => {});
  }, []);

  const active = PAIRS[idx];
  const before = media[`${active.key}-before`];
  const after = media[`${active.key}-after`];

  return (
    <section
      className="py-32 md:py-40 relative"
      style={{ background: "var(--bg)" }}
      data-testid="transformation-gallery"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        {/* Header rail */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-2 overline pt-2">Portfolio</div>
          <div className="md:col-span-10 flex items-end justify-between flex-wrap gap-6">
            <h2
              className="font-display text-5xl md:text-7xl"
              style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}
            >
              The{" "}
              <span
                className="font-display-italic"
                style={{ color: "var(--gold-2)" }}
              >
                transformation.
              </span>
            </h2>
            <div className="flex items-center gap-3">
              <div className="pill" data-testid="pair-counter">
                {String(idx + 1).padStart(2, "0")} / {String(PAIRS.length).padStart(2, "0")}
              </div>
              <div className="pill">Move cursor to inspect</div>
            </div>
          </div>
        </motion.div>

        {/* Two-column: slider + story */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          {/* Slider column */}
          <motion.div
            key={`slider-${active.key}`}
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-8"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.key}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {before && after ? (
                  <SpotlightBeforeAfter
                    before={before}
                    after={after}
                    label={active.label}
                    testid={`before-after-${active.key}`}
                  />
                ) : (
                  <div
                    className="relative w-full aspect-[16/10] flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.36em]"
                    style={{
                      borderRadius: "var(--r-2xl)",
                      border: "1px solid var(--line-gold)",
                      color: "var(--text-3)",
                      background: "linear-gradient(135deg, rgba(198,168,124,0.06), rgba(0,0,0,0.4))",
                    }}
                  >
                    Loading transformation…
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Story column */}
          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={`story-${active.key}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`transform-story-${active.key}`}
              >
                <div
                  className="font-mono text-[10px] uppercase tracking-[0.42em]"
                  style={{ color: "var(--gold)" }}
                >
                  {active.kicker}
                </div>
                <h3
                  className="mt-6 font-display leading-[0.98]"
                  style={{
                    fontSize: "clamp(2rem, 3.4vw, 3.2rem)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {active.headline}
                </h3>
                <p
                  className="mt-6 font-body"
                  style={{ color: "var(--text-2)", fontSize: 16, lineHeight: 1.7 }}
                >
                  {active.body}
                </p>
                <div
                  className="mt-10 pt-6"
                  style={{ borderTop: "1px solid var(--line)" }}
                >
                  <div
                    className="font-display leading-none"
                    style={{
                      fontSize: "clamp(2.5rem, 3.6vw, 3.5rem)",
                      color: "var(--gold-2)",
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {active.stat.value}
                  </div>
                  <div
                    className="mt-3 font-mono text-[10px] uppercase tracking-[0.38em]"
                    style={{ color: "var(--text-3)" }}
                  >
                    {active.stat.label}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="mt-10 flex items-center gap-3">
              <button
                onClick={() => setIdx((idx - 1 + PAIRS.length) % PAIRS.length)}
                className="inline-flex items-center justify-center w-11 h-11 transition-colors"
                style={{
                  borderRadius: "var(--r-full)",
                  border: "1px solid var(--line-gold)",
                  color: "var(--gold)",
                  background: "rgba(198,168,124,0.04)",
                }}
                data-cursor="hover"
                data-testid="transform-prev"
                aria-label="Previous transformation"
              >
                <ArrowLeft size={15} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => setIdx((idx + 1) % PAIRS.length)}
                className="inline-flex items-center justify-center w-11 h-11 transition-colors"
                style={{
                  borderRadius: "var(--r-full)",
                  border: "1px solid var(--line-gold)",
                  color: "var(--gold)",
                  background: "rgba(198,168,124,0.04)",
                }}
                data-cursor="hover"
                data-testid="transform-next"
                aria-label="Next transformation"
              >
                <ArrowRight size={15} strokeWidth={1.5} />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2 ml-4">
                {PAIRS.map((p, i) => (
                  <button
                    key={p.key}
                    onClick={() => setIdx(i)}
                    className="transition-all duration-500"
                    aria-label={`Show transformation ${i + 1}`}
                    data-testid={`transform-dot-${i}`}
                    style={{
                      width: i === idx ? 32 : 8,
                      height: 3,
                      borderRadius: 2,
                      background: i === idx ? "var(--gold)" : "var(--line)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contextual note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-14 font-mono text-[10px] uppercase tracking-[0.36em] text-center"
          style={{ color: "var(--text-3)" }}
        >
          Every job documented · Photographs delivered with each service report ·{" "}
          <a href="/gallery" className="underline underline-offset-4" style={{ color: "var(--gold)" }} data-testid="transform-gallery-link">
            See full gallery →
          </a>
        </motion.p>
      </div>
    </section>
  );
}
